import type { Cage, CageApiId } from '@/model/Cage.model';
import { Select } from '@/components/UI/FormFields/Latest';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useMemo } from 'react';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { successToast, errorToast } from '@/helpers';
import { FormProvider, useForm, Controller } from 'react-hook-form';
import { type StudyApiId } from '@/model/Study.model.ts';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';

interface CageReorderRequest {
  target_cage_api_id: CageApiId | undefined;
  position: 'before' | 'after';
}

interface SelectOption {
  value: CageApiId;
  label: string;
}

interface ReorderCageProps {
  /** Single cage from card-view callers. Prefer `cages` when available. */
  cage?: Cage;
  /** Selected cages to reorder (bulk or single). Takes precedence over `cage`. */
  cages?: Array<Cage>;
  selectOptions: Array<SelectOption>;
  studyApiId: StudyApiId;
  closeModal: () => void;
  fetchCages: () => void;
}

/**
 * Reorder one or more study cages relative to a target cage (before/after).
 * Used by Study Cages card view (single) and list view bulk actions.
 */
export const ReorderCage: FC<ReorderCageProps> = ({
  cage,
  cages,
  selectOptions,
  studyApiId,
  fetchCages,
  closeModal,
}) => {
  const selectedCages = useMemo(() => {
    if (_isNotEmpty(cages)) {
      return cages;
    }
    if (_notNil(cage)) {
      return [cage];
    }
    return [];
  }, [cage, cages]);

  const isBulk = selectedCages.length > 1;
  const primaryCage = selectedCages[0];

  const { invoke: reorderCage, loading: isReorderingSingle } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/cages/reorder',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const { invoke: reorderCages, loading: isReorderingMany } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/cages/reorder-many',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false } },
  });

  const isSubmitting = isReorderingSingle || isReorderingMany;

  const onSubmit = async (update: CageReorderRequest) => {
    if (_isNil(update.target_cage_api_id) || _isEmpty(selectedCages)) {
      errorToast('Please select a target cage');
      return;
    }

    const targetCageApiId = update.target_cage_api_id;
    const failureMessage = isBulk ? 'Failed to reorder cages' : 'Failed to reorder cage';

    try {
      const reorderResponse = isBulk
        ? await reorderCages({
            body: {
              cage_api_ids: selectedCages.map((selectedCage) => selectedCage.api_id),
              target_cage_api_id: targetCageApiId,
              position: update.position,
            },
            path: { studyApiId },
          })
        : await reorderCage({
            body: {
              cage_api_id: primaryCage.api_id,
              target_cage_api_id: targetCageApiId,
              position: update.position,
            },
            path: { studyApiId },
          });

      if (reorderResponse?.type === 'success') {
        successToast(isBulk ? 'Cages reordered successfully' : 'Cage reordered successfully');
        fetchCages();
        closeModal();
      } else {
        errorToast(failureMessage);
      }
    } catch {
      errorToast(failureMessage);
    }
  };

  const formMethods = useForm<CageReorderRequest>({
    mode: 'onChange',
    defaultValues: {
      position: 'before' as const,
      target_cage_api_id: undefined,
    },
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty, isSubmitting: formSubmitting },
  } = formMethods;

  const title = isBulk ? 'Reorder cages' : 'Reorder cage';
  const subText = isBulk ? `${selectedCages.length} cages selected` : (primaryCage?.name ?? '');
  const positionLabel = isBulk ? 'Move cages:' : `Move cage '${primaryCage?.name ?? ''}' to`;

  return (
    <ModalContainer size="narrow">
      <ModalHeader
        title={title}
        subText={subText}
        closeModal={closeModal}
        className="pa3 bb b--moon-gray bg-white"
        data-testid="modal-header"
      />
      <FormProvider {...formMethods}>
        <form data-test-component="ReorderCage" data-test-element="form" onSubmit={handleSubmit(onSubmit)}>
          <div data-test-element="position-section" className="ph3 pv2">
            <label className="mb2 db" data-testid="position-label">
              {positionLabel}
            </label>
            <Controller
              name="position"
              control={control}
              rules={{ required: 'Please select a position' }}
              render={({ field: { onChange, value } }) => (
                <div data-test-element="position-options" className="flex items-center">
                  <Radio
                    id="position-before"
                    name="position"
                    label="Before"
                    value="before"
                    checked={value === 'before'}
                    onChange={(e) => onChange(e.target.value)}
                    className="pointer"
                    dataTestId="position-before"
                    disabled={isSubmitting || formSubmitting}
                  />
                  <Radio
                    id="position-after"
                    name="position"
                    label="After"
                    value="after"
                    checked={value === 'after'}
                    onChange={(e) => onChange(e.target.value)}
                    className="pointer ml3"
                    dataTestId="position-after"
                    disabled={isSubmitting || formSubmitting}
                  />
                </div>
              )}
            />
          </div>
          <div data-test-element="cage-selection" className="ph3 pv2">
            <Select
              label="Cage"
              required
              name="target_cage_api_id"
              options={selectOptions}
              disabled={isSubmitting || formSubmitting}
            />
          </div>
        </form>
      </FormProvider>
      <ModalActions
        data-test-element="actions"
        className="pa3 mt3 bt b--moon-gray"
        onSubmit={handleSubmit(onSubmit)}
        onCancel={closeModal}
        submitButtonProps={{
          disabled: !isValid || !isDirty || isSubmitting || formSubmitting,
          testId: 'submit-button',
        }}
        cancelButtonProps={{
          disabled: isSubmitting || formSubmitting,
          testId: 'cancel-button',
        }}
      />
    </ModalContainer>
  );
};
